import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Paper,
  Typography,
  Box,
  Alert,
  useTheme,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Code,
  Copy,
  Check,
  Info,
  Terminal,
  Braces,
  ChevronDown,
  ChevronUp,
  Video,
  Link2Off,
} from 'lucide-react';
import { RootState } from '../../store';
import { useAuth } from '../../hooks';
import { useMeetingProviders } from '../../hooks/meeting/useMeetingProviders';
import { NotificationT, SEVERITY } from '../../types/notification';
import Notifications from '../../components/common/Notifications';

const GoogleMeetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M14.5 10.5L18.5 7.5C19 7.1 19.5 7.3 19.5 8V16C19.5 16.7 19 16.9 18.5 16.5L14.5 13.5V10.5Z"
      fill="#00832D"
    />
    <rect x="3.5" y="6" width="12" height="12" rx="2" fill="#00AC47" />
    <path
      d="M14.5 10.5L18.5 7.5C19 7.1 19.5 7.3 19.5 8V16C19.5 16.7 19 16.9 18.5 16.5L14.5 13.5V10.5Z"
      fill="#00832D"
    />
    <rect x="3.5" y="6" width="12" height="12" rx="2" fill="#00AC47" />
    <path d="M7.5 10.5H11.5V14.5H7.5V10.5Z" fill="white" opacity="0.3" />
  </svg>
);

const ZoomIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="4" fill="#2D8CFF" />
    <path d="M15.5 10L19 7.5V16.5L15.5 14V10Z" fill="white" />
    <rect x="4.5" y="8" width="10" height="8" rx="1.5" fill="white" />
  </svg>
);

const CodeBlock = ({ code, label }: { code: string; label: string }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
          <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
          borderRadius: 1,
          p: 2,
          position: 'relative',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          },
        }}
      >
        {code}
      </Box>
    </Box>
  );
};

const MeetingProvidersSection = () => {
  const {
    isLoading,
    isZoomConnected,
    isGoogleMeetConnected,
    getProviderEmail,
    connectZoom,
    connectGoogleMeet,
    disconnect,
  } = useMeetingProviders();

  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [disconnectDialog, setDisconnectDialog] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const handleConnect = async (provider: 'zoom' | 'google_meet') => {
    setConnectingProvider(provider);
    try {
      if (provider === 'zoom') {
        await connectZoom();
      } else {
        await connectGoogleMeet();
      }
      setNotification({
        open: true,
        message: `${provider === 'zoom' ? 'Zoom' : 'Google Meet'} connected successfully`,
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: `Failed to connect ${provider === 'zoom' ? 'Zoom' : 'Google Meet'}`,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConnectingProvider(null);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectDialog) return;
    try {
      await disconnect(disconnectDialog);
      setNotification({
        open: true,
        message: `${disconnectDialog === 'zoom' ? 'Zoom' : 'Google Meet'} disconnected`,
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to disconnect provider',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setDisconnectDialog(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />

      <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
        Online Meeting Integrations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your meeting providers to automatically create meeting links for online services.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          Meeting duration limits:
        </Typography>
        <Typography variant="body2">
          Zoom free accounts have a 40-minute limit for group meetings (3+ participants). Google
          Meet personal Gmail accounts have a 60-minute limit. Google Workspace accounts support
          longer meetings.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Google Meet Card */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 300,
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <GoogleMeetIcon />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Google Meet
              </Typography>
            </Box>
            <Chip
              label={isGoogleMeetConnected ? 'Connected' : 'Not Connected'}
              size="small"
              color={isGoogleMeetConnected ? 'success' : 'default'}
              variant={isGoogleMeetConnected ? 'filled' : 'outlined'}
            />
          </Box>

          {isGoogleMeetConnected && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getProviderEmail('google_meet')}
            </Typography>
          )}

          {isGoogleMeetConnected ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Link2Off size={16} />}
              onClick={() => setDisconnectDialog('google_meet')}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleConnect('google_meet')}
              disabled={connectingProvider === 'google_meet'}
              startIcon={
                connectingProvider === 'google_meet' ? <CircularProgress size={16} /> : undefined
              }
              sx={{ textTransform: 'none' }}
            >
              Connect Google Meet
            </Button>
          )}
        </Paper>

        {/* Zoom Card */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 300,
            p: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <ZoomIcon />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Zoom
              </Typography>
            </Box>
            <Chip
              label={isZoomConnected ? 'Connected' : 'Not Connected'}
              size="small"
              color={isZoomConnected ? 'success' : 'default'}
              variant={isZoomConnected ? 'filled' : 'outlined'}
            />
          </Box>

          {isZoomConnected && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {getProviderEmail('zoom')}
            </Typography>
          )}

          {isZoomConnected ? (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Link2Off size={16} />}
              onClick={() => setDisconnectDialog('zoom')}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleConnect('zoom')}
              disabled={connectingProvider === 'zoom'}
              startIcon={connectingProvider === 'zoom' ? <CircularProgress size={16} /> : undefined}
              sx={{ textTransform: 'none' }}
            >
              Connect Zoom
            </Button>
          )}
        </Paper>
      </Box>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={!!disconnectDialog} onClose={() => setDisconnectDialog(null)}>
        <DialogTitle>Disconnect Provider</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to disconnect{' '}
            {disconnectDialog === 'zoom' ? 'Zoom' : 'Google Meet'}? Existing bookings with meeting
            links will not be affected, but new bookings will not generate meeting links for this
            provider.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog(null)}>Cancel</Button>
          <Button onClick={handleDisconnect} color="error" variant="contained">
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const IntegrationsSettings = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const organization = useSelector((state: RootState) => state.organizations.selectedOrganisation);
  const [activeTab, setActiveTab] = useState(0);
  const [apiPanelOpen, setApiPanelOpen] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (!isAuthenticated) return null;

  if (!organization) {
    return (
      <Paper elevation={0} sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization to view integrations.</Alert>
      </Paper>
    );
  }

  const showMeetingsTab =
    (organization.isBookingEnabled ?? false) && (organization.isOnlineMeetingEnabled ?? false);

  let scriptUrl = 'https://snippet.cacxi.com.au/cacxi-script.js';
  switch (import.meta.env.VITE_AWS_ENV) {
    case 'prod':
      scriptUrl = 'https://snippet.cacxi.com.au/cacxi-script.js';
      break;
    case 'demo':
      scriptUrl = 'https://snippet.demo.cacxi.com/cacxi-script.js';
      break;
    default:
      scriptUrl = 'https://snippet.cacxi.com.au/cacxi-script.js';
      break;
  }

  const codeBlock = `<script
    src="${scriptUrl}"
    cacxi-id="${organization.id}"
    defer>
  </script>`;

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'theme.palette.background.paper',
        borderRadius: 1,
        border: '1px solid theme.palette.divider',
      }}
      className="w-full xl:max-w-4xl mx-auto mt-10"
    >
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
        }}
      >
        <Tab
          icon={<Code size={18} />}
          iconPosition="start"
          label="Integrate on your website"
          sx={{ textTransform: 'none', minHeight: 56 }}
        />
        {showMeetingsTab && (
          <Tab
            icon={<Video size={18} />}
            iconPosition="start"
            label="Online meeting setup"
            sx={{ textTransform: 'none', minHeight: 56 }}
          />
        )}
      </Tabs>

      {/* Tab Panel: Paste your chatbot */}
      {activeTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
              Website Integration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add the following code snippet to your website's HTML to enable the chat widget
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
            className="snippet-tour"
          >
            <Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  backgroundColor: `${theme.palette.primary.main}15`,
                  color: theme.palette.primary.main,
                  p: 1,
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Code size={20} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Copy and paste this code into your website's HTML, just before the closing{' '}
                <code>&lt;/body&gt;</code> tag
              </Typography>

              <CodeBlock code={codeBlock} label="" />
            </Box>
          </Paper>

          {/* JavaScript API Panel */}
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Header — clickable to expand/collapse */}
            <Box
              onClick={() => setApiPanelOpen(prev => !prev)}
              sx={{
                px: 3,
                py: 2,
                borderBottom: apiPanelOpen ? `1px solid ${theme.palette.divider}` : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                  backgroundColor:
                    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  backgroundColor: `${theme.palette.warning.main}15`,
                  color: theme.palette.warning.main,
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Terminal size={20} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  JavaScript API
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Control the chat widget programmatically using{' '}
                  <code style={{ fontWeight: 600 }}>window.cacxi</code>
                </Typography>
              </Box>
              {apiPanelOpen ? (
                <ChevronUp size={20} color={theme.palette.text.secondary} />
              ) : (
                <ChevronDown size={20} color={theme.palette.text.secondary} />
              )}
            </Box>

            <Collapse in={apiPanelOpen}>
              <Box sx={{ p: 3 }}>
                {/* Properties Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Braces size={16} color={theme.palette.info.main} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Properties
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      {
                        api: 'window.cacxi.theme',
                        returns: '"light" | "dark"',
                        desc: 'Returns the current theme mode',
                      },
                      {
                        api: 'window.cacxi.fullscreen',
                        returns: 'boolean',
                        desc: 'Returns whether the widget is in fullscreen mode',
                      },
                    ].map(item => (
                      <Box
                        key={item.api}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          px: 2,
                          py: 1.25,
                          borderRadius: 1.5,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.03)'
                              : 'rgba(0,0,0,0.02)',
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: theme.palette.info.main,
                            whiteSpace: 'nowrap',
                            minWidth: 220,
                            pt: 0.25,
                          }}
                        >
                          {item.api}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ fontSize: '0.8rem' }}
                          >
                            {item.desc}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: theme.palette.success.main,
                              fontSize: '0.72rem',
                            }}
                          >
                            → {item.returns}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Methods Section */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Code size={16} color={theme.palette.warning.main} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Methods
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      {
                        api: 'window.cacxi.toggleTheme()',
                        desc: 'Toggle between light and dark mode',
                      },
                      {
                        api: 'window.cacxi.setDarkTheme()',
                        desc: 'Force dark mode on the chat widget',
                      },
                      {
                        api: 'window.cacxi.setLightTheme()',
                        desc: 'Force light mode on the chat widget',
                      },
                      {
                        api: 'window.cacxi.openChat()',
                        desc: 'Open the chat widget',
                      },
                      {
                        api: 'window.cacxi.openChat("Hello")',
                        desc: 'Open the chat widget and send a message',
                      },
                      {
                        api: 'window.cacxi.openChat("Hello", true)',
                        desc: 'Open the chat in fullscreen mode and send a message',
                      },
                    ].map(item => (
                      <Box
                        key={item.api}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          px: 2,
                          py: 1.25,
                          borderRadius: 1.5,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.03)'
                              : 'rgba(0,0,0,0.02)',
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            color: theme.palette.warning.main,
                            whiteSpace: 'nowrap',
                            minWidth: 320,
                            pt: 0.25,
                          }}
                        >
                          {item.api}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ fontSize: '0.8rem', flex: 1 }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Usage Example */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Usage Example
                  </Typography>
                  <CodeBlock
                    code={`// Open chat with a pre-filled message
window.cacxi.openChat("I need help with my booking");

// Open chat in fullscreen with a message
window.cacxi.openChat("Hello", true);

// Toggle dark mode
window.cacxi.toggleTheme();

// Check current state
console.log(window.cacxi.theme);      // "light" or "dark"
console.log(window.cacxi.fullscreen);  // true or false`}
                    label="Add to your website's JavaScript"
                  />
                </Box>

                {/* Info Note */}
                <Alert severity="info" sx={{ mt: 1 }} icon={<Info size={18} />}>
                  <Typography variant="caption">
                    The <code>window.cacxi</code> object is available after the snippet script has
                    loaded. Make sure to call these methods after the page has finished loading, or
                    inside a <code>DOMContentLoaded</code> / <code>window.onload</code> handler.
                  </Typography>
                </Alert>
              </Box>
            </Collapse>
          </Paper>
        </Box>
      )}

      {/* Tab Panel: Meeting Integrations */}
      {showMeetingsTab && activeTab === 1 && (
        <Box sx={{ p: 3 }}>
          <MeetingProvidersSection />
        </Box>
      )}
    </Paper>
  );
};

export default IntegrationsSettings;
